@if (vm(); as vm) {
  <div class="tt-smartbook-header">
    <div class="tt-smartbook-header_header">
      <h1
        class="tt-smartbook-header_header-title"
        ttCommentableElement
        [elementId]="`${guidelineData().uniqueId}_title`"
        [widgetId]="guidelineData().uniqueId">
        {{ vm.title }}
      </h1>

      @if (organisationImage) {
        <img alt="{{ 'OrganizationImage' | transloco }}" class="tt-smartbook-header-logo" [src]="organisationImage" />
      }
    </div>

    <div class="tt-smartbook-header-resource-tags">
      <tt-resource-tag-list
        [readonly]="!guidelineViewService?.allowEditTags()"
        [tags]="guidelineData().resourceTags ?? []"
        (tagRemoved)="removeTag($event)" />

      @if (guidelineViewService?.allowEditTags()) {
        <button
          #tagTriggerButton
          aria-label="Add a Resource Tag"
          ttButton
          [emphasis]="'minimal'"
          [size]="'small'"
          [variant]="'secondary'"
          (click)="openTagDropdown($event)">
          <mat-icon [icon]="'plus'" [iconSize]="14" />
          @if (!guidelineData().resourceTags?.length) {
            {{ 'ResourceTag.AddTag' | transloco }}
          }
        </button>
      }
    </div>

    <ng-template #addResourceTagsTemplate>
      <tt-add-resource-tags [selectedTags]="guidelineData().resourceTags ?? []" (tagsChanged)="handleTagsChanged($event)" />
    </ng-template>

    @if (vm.additionalInfo) {
      <common-rich-text-view
        class="header-content"
        id="header-froala-view"
        ttCommentableText
        [content]="vm.additionalInfo"
        [elementId]="`${guidelineData().uniqueId}-text`"
        [widgetId]="guidelineData().uniqueId" />
    }

    <div class="tt-smartbook-header_info">
      <div class="tt-smartbook-header_authors">
        @if (vm.owner && (vm.owner?.firstName?.length > 0 || vm.owner?.lastName?.length > 0)) {
          <div class="tt-smartbook-info owner">
            <span class="label">{{ 'GuidelineOwner' | transloco }}:</span>
            <span class="input">
              {{ vm.owner | name }}
              @if (vm.owner?.email?.length > 0) {
                <a class="mail-link" href="mailto:{{ vm.owner.email }}"><mat-icon [icon]="'mail'" [iconSize]="16" /></a>
              }
            </span>
          </div>
        }
      </div>
      @if (vm?.authors?.length > 0) {
        <div class="tt-smartbook-header_authors">
          <div class="tt-smartbook-info">
            <span class="label">{{ 'GuidelineAuthors' | transloco }}:</span>
            @for (author of vm.authors; track author) {
              <span class="input">
                {{ author | name }}
                @if (author?.email?.length > 0) {
                  <a class="mail-link" href="mailto:{{ author.email }}"><mat-icon [icon]="'mail'" [iconSize]="16" /></a>
                }
              </span>
            }
          </div>
        </div>
      }

      @if (isRevisionDateEnabled() && vm.revisionDate) {
        <div
          class="tt-smartbook-header__revision-date tt-smartbook-info"
          [class.tt-smartbook-header__revision-date--expired]="vm.isExpired"
          [class.tt-smartbook-header__revision-date--expiring]="vm.isExpiringSoon">
          <span class="label">{{ 'GuidelineEditor.Header.RevisionDate.Label' | transloco }}:</span>
          <span class="input">
            {{ vm.revisionDate | addZToUTC | translocoDate }}
          </span>
          @if (vm.isExpired) {
            <mat-icon
              [icon]="'error'"
              [iconSize]="13"
              [ttTooltip]="
                'GuidelineManage.ManageTable.ExpiredTooltip' | transloco: { date: vm.revisionDate | addZToUTC | translocoDate }
              " />
          } @else if (vm.isExpiringSoon) {
            <mat-icon
              [icon]="'warning'"
              [iconSize]="15"
              [ttTooltip]="
                'GuidelineManage.ManageTable.ExpiringTooltip' | transloco: { date: vm.revisionDate | addZToUTC | translocoDate }
              " />
          }
        </div>
      }

      <div class="organisation_version tt-smartbook-info">
        <app-key-value key="{{ 'Organisation' | transloco }}" value="{{ vm.organisation ? vm.organisation.displayName : '-' }}" />
        <app-key-value key="{{ 'Version' | transloco }}" value="{{ vm.version ? vm.version : '-' }}" />
        @if (!isPreviewMode && (vm.updateMessage || vm.isPublishedWithTags)) {
          <span class="tt-smartbook-header_update-message-trigger" (click)="onWhatsNewClick()">
            {{ 'GuidelineEditor.Header.UpdateDialogTriggerText' | transloco }}
            <mat-icon [icon]="'info'" />
          </span>
        }
      </div>

      @if (vm.guidelineOrigin === GuidelineOrigin.SHARED && vm.isAutomaticUpdateEnabled) {
        <div class="datetimes tt-smartbook-info">
          <app-key-value
            key="{{ 'SecondApproval' | transloco }}"
            value="{{
              (guidelineData().connectedGuidelineInformation?.originalGuidelineInformation?.publisher | name) +
                ' (' +
                (guidelineData().connectedGuidelineInformation?.originalGuidelineInformation?.publishedAt | utcToLocal) +
                ')'
            }}" />
        </div>
      } @else {
        <div class="datetimes tt-smartbook-info">
          <app-key-value key="{{ 'LastEdit' | transloco }}" value="{{ vm.editString }}" />
          <app-key-value key="{{ 'FirstApproval' | transloco }}" value="{{ vm.approvedString }}" />
          <app-key-value key="{{ 'SecondApproval' | transloco }}" value="{{ vm.publishedString }}" />
        </div>
      }

      @if (vm.reviewerString) {
        <div class="datetimes tt-smartbook-info">
          <app-key-value key="{{ 'Reviewers' | transloco }}" value="{{ vm.reviewerString }}" />
        </div>
      }

      @if (vm | isConnectedGuideline) {
        <div class="tt-smartbook-header_original">
          <div class="tt-smartbook-header_original-data">
            <div class="tt-smartbook-header_original-title">{{ 'ImportedGuidelineInfo.OriginalInfo' | transloco }}</div>
            <div class="tt-smartbook-header_original-field">
              <span class="tt-smartbook-header_original-field-label">{{ 'ImportedGuidelineInfo.Owner' | transloco }}:</span>
              <span class="tt-smartbook-header_original-field-value">{{ vm.originalGuidelineInfo.owner | name }}</span>
            </div>
            @if (vm.originalGuidelineInfo.authors?.length > 0) {
              <div class="tt-smartbook-header_original-field">
                <span class="tt-smartbook-header_original-field-label">{{ 'ImportedGuidelineInfo.Authors' | transloco }}:</span>
                <span class="tt-smartbook-header_original-field-value">
                  @for (author of vm.originalGuidelineInfo.authors?.slice(0, 6); track author.id) {
                    <span class="tt-imported-guideline-info_data-value tt-imported-guideline-info_data-value-author">
                      {{ author | name }}{{ !$last ? ',' : '' }}&nbsp;
                    </span>
                  }

                  @if (vm.originalGuidelineInfo.authors?.length > 6) {
                    ...
                  }
                </span>
              </div>
            }
            <div class="tt-smartbook-header_original-line">
              <div class="tt-smartbook-header_original-field">
                <span class="tt-smartbook-header_original-field-label">
                  {{ 'ImportedGuidelineInfo.OriginalGuidelineFrom' | transloco }}:
                </span>
                <span class="tt-smartbook-header_original-field-value">{{ vm.originalGuidelineInfo.organization?.displayName }}</span>
              </div>
              @if (vm.originalGuidelineInfo.originalVersion) {
                <div class="tt-smartbook-header_original-field">
                  <span class="tt-smartbook-header_original-field-label">{{ 'ImportedGuidelineInfo.OriginalVersion' | transloco }}:</span>
                  <span class="tt-smartbook-header_original-field-value">{{ vm.originalGuidelineInfo.originalVersion }}</span>
                </div>
              }
            </div>
          </div>
          <img
            alt="{{ 'OrganizationImage' | transloco }}"
            class="tt-smartbook-header-logo"
            [src]="vm.originalGuidelineInfo.organization?.id | wrapFn: getOrganisationImage" />
        </div>
      }
    </div>
  </div>
}
